import Image from 'next/image'
import { useState } from 'react'

import { stegaClean } from 'next-sanity'

import { Button } from '@/app/components/Button'
import { Icon } from '@/app/components/Icon'
import { getLinkLabel, Link } from '@/app/components/Link'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { NavQueryResult } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

interface NavigationItemProps {
  classNames?: {
    root?: string
    button?: string
    description?: string
    link?: string
    collapseButton?: string
    collapseIndicator?: string
    itemOpened?: string
  }
  subItemClassNames?: {
    list?: string
    listOpened?: string
    root?: string
    button?: string
    description?: string
    link?: string
  }
  collapsible?: boolean
  defaultOpen?: boolean
  ignoreButtonStyles?: boolean
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
  includeDescription?: boolean
  item: NonNullable<NavQueryResult[number]['menu']['items']>[number] | null
  themeMode?: 'light' | 'dark'
  tracking: TrackingLocation
  variantMapping?: Record<string, string>
}

export const NavigationItem = function ({
  classNames,
  subItemClassNames,
  collapsible = false,
  defaultOpen = false,
  ignoreButtonStyles = false,
  isOpen: controlledIsOpen,
  onToggle,
  includeDescription,
  item,
  themeMode,
  tracking,
  variantMapping,
}: NavigationItemProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen)

  if (!item) {
    return null
  }

  const hasChildren = item.children && item.children.length > 0

  // Use controlled state if provided, otherwise use uncontrolled state
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    const newState = !isOpen

    if (isControlled && onToggle) {
      // Controlled mode (accordion) - notify parent
      onToggle(newState)
    } else {
      // Uncontrolled mode (independent) - manage own state
      setUncontrolledIsOpen(newState)
    }
  }

  const renderLinkContent = () => (
    <>
      {getLinkLabel(item.link)}
      {includeDescription && item.description && (
        <span className={cn('', classNames?.description)}>{item.description}</span>
      )}
      {collapsible && hasChildren && (
        <Icon
          name="faAngleDown"
          provider="fontawesome"
          style="regular"
          size={16}
          className={cn(
            'transition-transform duration-200',
            isOpen && 'scale-y-[-1]',
            classNames?.collapseIndicator,
          )}
        />
      )}
    </>
  )

  return (
    <li
      key={item._key}
      className={cn(
        'relative',
        classNames?.root,
        collapsible && hasChildren && isOpen && classNames?.itemOpened,
      )}
    >
      {item.itemType === 'link' && item.link ? (
        collapsible && hasChildren ? (
          <button
            type="button"
            onClick={handleToggle}
            className={cn('', classNames?.link, classNames?.collapseButton)}
          >
            {renderLinkContent()}
          </button>
        ) : hasChildren && !item.link.href ? (
          <span
            tabIndex={0}
            role="button"
            aria-haspopup="true"
            aria-expanded={isOpen}
            className={cn('', classNames?.link)}
          >
            {renderLinkContent()}
          </span>
        ) : (
          <Link link={item.link} tracking={tracking} className={cn('', classNames?.link)}>
            {renderLinkContent()}
          </Link>
        )
      ) : item.itemType === 'image' && item.image?.image?.asset && item.image?.link ? (
        (() => {
          const navImage = item.image.image
          const imageUrl = urlForImage(navImage)?.url()
          if (!imageUrl) return null
          return (
            <Link link={item.image.link} tracking={tracking} className={cn('', classNames?.link)}>
              <Image
                loader={({ width }) => urlForImage(navImage)?.width(width).url() || imageUrl}
                src={imageUrl}
                alt={stegaClean(navImage.alt) || ''}
                width={400}
                height={100}
                className="h-auto w-full max-w-[368px] lg:max-w-full"
                sizes="(max-width: 768px) 100vw, 400px"
                loading="lazy"
              />
            </Link>
          )
        })()
      ) : item.itemType === 'button' && item.button ? (
        ignoreButtonStyles && collapsible && hasChildren ? (
          <button
            type="button"
            onClick={handleToggle}
            className={cn('', classNames?.link, classNames?.collapseButton)}
          >
            {item.button.link?.label ||
              item.button.link?.internalPage?.name ||
              item.button.link?.internalPost?.title ||
              'Button'}
            {includeDescription && item.description && (
              <span className={cn('', classNames?.description)}>{item.description}</span>
            )}
            <Icon
              name="faAngleDown"
              provider="fontawesome"
              style="regular"
              size={16}
              className={cn(
                'transition-transform duration-200',
                isOpen && 'scale-y-[-1]',
                classNames?.collapseIndicator,
              )}
            />
          </button>
        ) : collapsible && hasChildren ? (
          <Button
            button={item.button}
            tracking={tracking}
            themeMode={themeMode}
            className={cn('', ignoreButtonStyles ? classNames?.link : classNames?.button)}
            ignoreVariantStyles={ignoreButtonStyles}
            variantMapping={variantMapping}
          >
            {item.button.link?.label ||
              item.button.link?.internalPage?.name ||
              item.button.link?.internalPost?.title ||
              'Button'}
            <Icon
              name="faAngleDown"
              provider="fontawesome"
              style="regular"
              size={16}
              className={cn(
                'transition-transform duration-200',
                collapsible && isOpen && 'scale-y-[-1]',
                'group-hover:scale-y-[-1]',
                classNames?.collapseIndicator,
              )}
            />
          </Button>
        ) : (
          <Button
            button={item.button}
            tracking={tracking}
            themeMode={themeMode}
            className={cn('', ignoreButtonStyles ? classNames?.link : classNames?.button)}
            ignoreVariantStyles={ignoreButtonStyles}
            variantMapping={variantMapping}
          />
        )
      ) : null}
      {hasChildren && (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-in-out overflow-hidden',
            collapsible && !isOpen ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <ul
            role="list"
            className={cn(
              'overflow-hidden',
              subItemClassNames?.list,
              isOpen && subItemClassNames?.listOpened,
            )}
          >
            {item.children?.map((child) => (
              <li key={child._key} className={cn('', subItemClassNames?.root)}>
                {child.itemType === 'link' && child.link ? (
                  <Link
                    link={child.link}
                    tracking={tracking}
                    className={cn('', subItemClassNames?.link)}
                  >
                    {getLinkLabel(child.link)}
                    {includeDescription && child.description && (
                      <span className={cn('', subItemClassNames?.description)}>
                        {child.description}
                      </span>
                    )}
                  </Link>
                ) : child.itemType === 'image' && child.image?.image?.asset && child.image?.link ? (
                  (() => {
                    const navImage = child.image.image
                    const imageUrl = urlForImage(navImage)?.url()
                    if (!imageUrl) return null
                    return (
                      <Link
                        link={child.image.link}
                        tracking={tracking}
                        className={cn('', subItemClassNames?.link)}
                      >
                        <Image
                          loader={({ width }) =>
                            urlForImage(navImage)?.width(width).url() || imageUrl
                          }
                          src={imageUrl}
                          alt={stegaClean(navImage.alt) || ''}
                          width={400}
                          height={100}
                          className="h-auto w-full max-w-[368px] lg:max-w-full"
                          sizes="(max-width: 768px) 100vw, 400px"
                          loading="lazy"
                        />
                      </Link>
                    )
                  })()
                ) : child.itemType === 'button' && child.button ? (
                  <Button
                    button={child.button}
                    tracking={tracking}
                    themeMode={themeMode}
                    className={cn(
                      '',
                      ignoreButtonStyles ? subItemClassNames?.link : subItemClassNames?.button,
                    )}
                    ignoreVariantStyles={ignoreButtonStyles}
                    variantMapping={variantMapping}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}
